import { type CSSProperties, memo, useMemo } from 'react';

import { type Clip } from '@air/api/types';

import { type Dimension, getSpriteStyles } from '~/utils/videoThumbnails';

type VideoStaticThumbnailProps = {
  clip: Clip;
  containerDimensions: Dimension;
};

export const VideoStaticThumbnail = memo(({ clip, containerDimensions }: VideoStaticThumbnailProps) => {
  const thumbnailStyles = useMemo(() => {
    const thumbnail = clip.position?.start.thumbnail ?? 0;

    if (!clip.assets.spriteSheet) {
      return;
    }

    const spriteStyles = getSpriteStyles({
      containerWidth: containerDimensions.width,
      containerHeight: containerDimensions.height,
      spritesheet: clip.assets.spriteSheet,
      frame: thumbnail,
    });

    return {
      ...spriteStyles,
      backgroundImage: `url("${clip.assets.spriteSheet.src}")`,
    } satisfies CSSProperties;
  }, [clip, containerDimensions.width, containerDimensions.height]);

  return (
    // The radius reads the --gallery-card-inner-radius var (set by the public collection page's
    // Corner rounding setting — see shareLinkThumbnailRadius.ts) and falls back to the native 4px,
    // so every surface that doesn't set the var renders exactly as before.
    <div className="pointer-events-none absolute inset-0 size-full overflow-hidden rounded-[var(--gallery-card-inner-radius,4px)]">
      <div style={thumbnailStyles} />
    </div>
  );
});

VideoStaticThumbnail.displayName = 'VideoStaticThumbnail';
